import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, useTheme, useMediaQuery } from '@mui/material';
import ApprovalFilter from './components/ApprovalFilter';
import IndigentList from './components/IndigentList';
import { approvalService, ApprovalFilters } from '../../services/approvalService';
import { transformApiDataToUI } from './utils';
import { ApprovalData } from './types';

const ITEMS_PER_PAGE = 10;

const Approval = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isLargeTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    // Filter states
    const [selectedTab, setSelectedTab] = useState(-1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchField, setSearchField] = useState('Electricity Meter No');

    // Pagination state
    const [page, setPage] = useState(1);

    // Data states
    const [data, setData] = useState<ApprovalData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalItems, setTotalItems] = useState(0);
    const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

    // Map tab indices to API status values
    const getStatusFromTab = (tabIndex: number): string | undefined => {
        switch (tabIndex) {
            case -1:
                return undefined; // No filter selected
            case 0:
                return 'pending'; // Not Reviewed
            case 1:
                return 'reviewed'; // Review Passed
            case 2:
                return 'approved'; // Fully Approved
            case 3:
                return 'failed'; // Review Failed
            case 4:
                return 'rejected'; // Rejected
            default:
                return undefined;
        }
    };

    // Map search field to API parameter
    const getSearchFieldParam = (field: string): string => {
        switch (field) {
            case 'Electricity Meter No':
                return 'electricity_meter_no';
            case 'Personal ID':
                return 'sa_id';
            case 'Account Number':
                return 'account_number';
            case 'Indigent Number':
                return 'indigent_number';
            default:
                return 'electricity_meter_no';
        }
    };

    // Fetch data from API
    const fetchData = useCallback(async (searchValue?: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const filters: ApprovalFilters = {
                page,
                limit: ITEMS_PER_PAGE,
            };

            // Add status filter based on selected tab
            const status = getStatusFromTab(selectedTab);
            if (status) {
                filters.status = status as
                    | 'pending'
                    | 'reviewed'
                    | 'approved'
                    | 'failed'
                    | 'rejected';
            }

            // Add search filter if query exists
            const searchToUse = searchValue !== undefined ? searchValue : searchQuery;
            if (searchToUse.trim()) {
                const searchParam = getSearchFieldParam(searchField);
                if (searchParam === 'electricity_meter_no') {
                    filters.electricity_meter_no = searchToUse.trim();
                } else if (searchParam === 'sa_id') {
                    filters.sa_id = searchToUse.trim();
                } else if (searchParam === 'account_number') {
                    filters.account_number = searchToUse.trim();
                } else if (searchParam === 'indigent_number') {
                    filters.indigent_number = searchToUse.trim();
                }
            }

            const response = await approvalService.getApprovals(filters);

            // Transform API data to UI format
            const transformedData = response.data.map(transformApiDataToUI);

            setData(transformedData);
            setTotalItems(response.total);
        } catch (error) {
            console.error('Error fetching approvals:', error);
            setError('Failed to fetch approvals. Please try again.');
            setData([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [page, selectedTab, searchQuery, searchField]);

    // Fetch data when dependencies change (excluding searchField)
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTabChange = (tab: number) => {
        // If clicking on the already selected tab, deselect it
        if (selectedTab === tab) {
            setSelectedTab(-1);
        } else {
            setSelectedTab(tab);
        }
        setPage(1); // Reset to first page when changing tabs
    };

    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
        // Don't reset page or fetch data here - wait for user to actually search
    };

    const handleSearchFieldChange = (field: string) => {
        setSearchField(field);
        // Don't reset page or fetch data here - wait for user to actually search
    };

    const handleSearch = () => {
        setPage(1); // Reset to first page when searching
        fetchData(); // Fetch data with current search query
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleStatusUpdate = async (
        requestId: string,
        status: 'reviewed' | 'failed' | 'approved' | 'rejected',
        remark?: string,
        file?: File,
    ) => {
        setUpdatingStatus(requestId);
        try {
            await approvalService.updateApprovalStatus(requestId, status, remark, file);

            // Refresh the data after successful status update
            await fetchData();

            // Show success message (you can add a toast notification here)
            console.log(`Status updated successfully to ${status}`);
        } catch (error) {
            console.error('Error updating approval status:', error);
            setError('Failed to update approval status. Please try again.');
        } finally {
            setUpdatingStatus(null);
        }
    };

    return (
        <div style={{
            backgroundColor: '#FEFBFF',
            minHeight: window.innerWidth < 600 ? 'calc(100vh - 96px)' : '100vh',
            position: window.innerWidth < 600 ? 'absolute' : 'relative',
            top: window.innerWidth < 600 ? '86px' : 'auto',
            left: window.innerWidth < 600 ? '0' : 'auto',
            right: window.innerWidth < 600 ? '0' : 'auto',
            bottom: window.innerWidth < 600 ? '0' : 'auto',
            padding: isTablet || isLargeTablet ? '0px' : '16px',
            paddingBottom: window.innerWidth < 600 ? '32px' : (isTablet || isLargeTablet ? '32px' : '16px'),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: window.innerWidth < 600 ? '100vw' : '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden'
        }}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: isSmallScreen?'16px':'32px',
                paddingBottom: window.innerWidth < 600 ? '24px' : (isTablet || isLargeTablet ? '32px' : '16px'),
                width: '100%',
                maxWidth: isTablet ? '95%' : (isLargeTablet ? '90%' : '100%'),
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: window.innerWidth < 600 ? '16px' : '0'
            }}>
                <Typography
                    //variant={isSmallScreen ? 'h6' : (isTablet || isLargeTablet ? 'h5' : 'h4')}
                    fontWeight={600}
                    gutterBottom
                    sx={{ 
                        mb: { xs: 1.5, sm: 2, md: 2.5 },
                        fontSize: { 
                            xs: '1.25rem', 
                            sm: '1.4rem', 
                            md: '1.75rem',
                            lg: '2rem'
                        }
                    }}
                    color='#132F5F'
                    fontFamily='Poppins, sans-serif'
                >
                    Approvals
                </Typography>

                {error && (
                    <Alert severity='error' sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ 
                    width: '100%', 
                    overflow: 'hidden', 
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                }}>
                    <ApprovalFilter
                        selectedTab={selectedTab}
                        searchQuery={searchQuery}
                        searchField={searchField}
                        onTabChange={handleTabChange}
                        onSearchChange={handleSearchChange}
                        onSearchFieldChange={handleSearchFieldChange}
                        onSearch={handleSearch}
                    />
                </Box>

                <IndigentList
                    data={data}
                    page={page}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={handlePageChange}
                    isLoading={isLoading}
                    onStatusUpdate={handleStatusUpdate}
                    updatingStatus={updatingStatus}
                />
            </div>
        </div>
    );
};

export default Approval;
